import { expectTypeOf } from 'expect-type';

import defined from './defined';

/**
 * Return the specified object key after traversal, or undefined.
 * This function will also unwrap any observables and call all functions along the way, but not the final result.
 * @param object - The object to traverse.
 * @param args - The keys to search for. If the last value is exactly true, the last key will be created as observable
 *   if it does not yet exist.
 * @example
 * let ob1 = {a: {b: {c: 5}}};
 * let ob2 = objectTraverse(ob1, 'a', 'b', 'c'); // 5
 * let ob3 = objectTraverse(ob1, 'd', 'b', 'c'); // undefined
 * let ob4 = ob1.d.b.c; // TypeError: Cannot read property 'b' of undefined.
 * @example
 * let obj = {a: ko.observable(5)};
 * let obs = objectTraverse(obj, 'a'); // observable.
 * let val = obs(); // 5.
 */
export default function objectTraverse<T, K extends [...(string | number)[]] | [...(string | number)[], true]>(
    object: T,
    ...args: K
): K extends [...infer U, true] ? NonNullable<Traverse<T, U>> : Traverse<T, K> {
    const result = _objectTraverse(object, ...args);
    _objectTraverse.prevobject = null;
    return result as K extends [...infer U, true] ? NonNullable<Traverse<T, U>> : Traverse<T, K>;
}

/**
 * Helper function for [[`objectTraverse`]] and [[`unwrap`]].
 * @param object - The object to traverse.
 * @param args - The keys to search for. If the last value is exactly true, the last key will be created as observable
 *   if it does not yet exist.
 */
function _objectTraverse<T, K extends (string | number)[]>(object: T, ...args: [...K] | [...K, true]): Traverse<T, K> {
    _objectTraverse.prevobject = null;
    // eslint-disable-next-line unicorn/prefer-at
    const creating = arguments[arguments.length - 1] === true; // If last argument is exactly true then it is not a key.
    const numkeys = arguments.length - 1 - (creating ? 1 : 0);
    const keys = args as K;
    let notifyChange;
    let lastobs;
    for (let i = 0; i < numkeys; i++) {
        if (creating && window.ko.isWritableObservable(object) && !defined(object())) {
            object({});
        }
        lastobs = window.ko.isObservable(object) ? object : lastobs;
        object = typeof object === 'function' ? object.call(_objectTraverse.prevobject) : object;
        if (object === null || (typeof object !== 'object' && typeof object !== 'function')) {
            return undefined as Traverse<T, K>;
        }
        const key = keys[i] as keyof typeof object;
        if (!(key in object)) {
            if (!creating) {
                return undefined as Traverse<T, K>;
            }
            notifyChange ||= lastobs;
            object[key] = (i + 1 === numkeys ? window.ko.observable() : {}) as (typeof object)[keyof typeof object];
        }
        _objectTraverse.prevobject = object;
        object = object[key] as unknown as typeof object;
    }
    if (notifyChange) {
        notifyChange.valueHasMutated();
    }
    return object as Traverse<T, K>;
}
_objectTraverse.prevobject = undefined as unknown;
type UnwrapSingle<T> =
    T extends ko.Observable<infer R>
        ? R
        : T extends { (...args: unknown[]): infer R }
          ? R // Any normal function.
          : T extends { (): infer R; (...args: unknown[]): unknown }
            ? R
            : T; // Account for ko.Observable call overload.
type TraverseOnceHelper<T, K> = T extends T
    ? K extends keyof T
        ? T[K]
        : unknown extends T
          ? unknown
          : undefined
    : never;
type TraverseOnce<T, K> = TraverseOnceHelper<UnwrapSingle<T>, K>;
type Traverse<T, K> = K extends [infer M, ...infer N] ? Traverse<TraverseOnce<T, M>, N> : T;

/**
 * Similar to a call to [[`objectTraverse`]] with the result unwrapped.
 * @param object - The object to traverse.
 * @param args - The keys to search for.
 */
function unwrap<T, K extends (string | number)[]>(
    object: T,
    ...args: [...K] | [...K, true]
): UnwrapSingle<Traverse<T, K>> {
    let result = _objectTraverse(object, ...args);
    result = typeof result === 'function' ? result.call(_objectTraverse.prevobject) : result;
    _objectTraverse.prevobject = null;
    return result as UnwrapSingle<Traverse<T, K>>;
}

export { unwrap as __unwrap };

// @ts-expect-error Unused, only for tests.
function _Tests() {
    // TraverseOnce
    expectTypeOf<TraverseOnce<{ x: number }, 'x'>>().toEqualTypeOf<number>();
    expectTypeOf<TraverseOnce<{ x: number } | { x: string }, 'x'>>().toEqualTypeOf<number | string>();
    expectTypeOf<TraverseOnce<() => { x: number }, 'x'>>().toEqualTypeOf<number>();
    expectTypeOf<TraverseOnce<(() => { x: number }) | (() => { x: string }), 'x'>>().toEqualTypeOf<number | string>();
    expectTypeOf<TraverseOnce<ko.Observable<{ x: number } | undefined>, 'x'>>().toEqualTypeOf<number | undefined>();
    expectTypeOf<TraverseOnce<unknown, 'x'>>().toEqualTypeOf<unknown>();

    // Traverse
    expectTypeOf<
        Traverse<ko.Observable<{ x: Record<number, ko.ObservableArray<number>> } | undefined>, ['x', number]>
    >().toEqualTypeOf<ko.ObservableArray<number> | undefined>();

    // UnwrapSingle
    expectTypeOf<UnwrapSingle<(() => number) | (() => string)>>().toEqualTypeOf<number | string>();
    expectTypeOf<UnwrapSingle<ko.Observable<{ x: number } | undefined>>>().toEqualTypeOf<{ x: number } | undefined>();
}
